import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const PRIVACY_CONTENT = {
  title: 'Política de Privacidad',
  lastUpdated: 'Última actualización: Abril 2026',
  sections: [{
    heading: '1. Información que recopilamos',
    text: 'Recopilamos información que usted nos proporciona directamente al utilizar nuestro servicio, incluyendo datos de contacto, información profesional y currículums subidos para su optimización. También podemos recopilar datos de uso y analíticas para mejorar nuestra plataforma.'
  }, {
    heading: '2. Uso de la información',
    text: 'Utilizamos su información exclusivamente para proporcionar, mantener y mejorar The Ghost Resume Engine. Sus datos profesionales son procesados por sistemas de Inteligencia Artificial con el único propósito de optimizar su perfil para sistemas ATS.'
  }, {
    heading: '3. Protección de datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. No vendemos ni compartimos sus datos personales con terceros para fines de marketing.'
  }, {
    heading: '4. Sus derechos',
    text: 'Usted tiene derecho a acceder, corregir o eliminar su información personal en cualquier momento. Si desea ejercer estos derechos, puede hacerlo a través de la configuración de su cuenta.'
  }]
};
const TERMS_CONTENT = {
  title: 'Términos de Servicio',
  lastUpdated: 'Última actualización: Abril 2026',
  sections: [{
    heading: '1. Aceptación de los términos',
    text: 'Al acceder y utilizar The Ghost Resume Engine, usted acepta estar sujeto a estos Términos de Servicio. Si no está de acuerdo con alguna parte de los términos, no podrá acceder al servicio.'
  }, {
    heading: '2. Uso del servicio',
    text: 'Nuestro servicio proporciona herramientas de optimización de currículums basadas en IA. Usted se compromete a proporcionar información veraz y precisa sobre su experiencia profesional. No garantizamos la obtención de empleo ni resultados específicos.'
  }, {
    heading: '3. Propiedad intelectual',
    text: 'Todo el contenido, características y funcionalidades del servicio, incluyendo pero no limitado a texto, gráficos, logos y software, son propiedad exclusiva de The Ghost Resume Engine y están protegidos por leyes de derechos de autor.'
  }, {
    heading: '4. Limitación de responsabilidad',
    text: 'En ningún caso The Ghost Resume Engine será responsable por daños indirectos, incidentales, especiales, consecuentes o punitivos, incluyendo sin limitación, pérdida de beneficios, datos, uso, fondo de comercio u otras pérdidas intangibles.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeModal]);
  const content = activeModal === 'privacy' ? PRIVACY_CONTENT : TERMS_CONTENT;
  return <>
      <footer className="text-center text-slate-500 text-sm border-t border-navy-800 pt-8 pb-4" data-id="element-132">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4" data-id="element-133">
          <button onClick={() => setActiveModal('privacy')} className="hover:text-slate-300 transition-colors" data-id="element-134">
            Política de Privacidad
          </button>
          <span className="hidden sm:inline text-navy-700" data-id="element-135">•</span>
          <button onClick={() => setActiveModal('terms')} className="hover:text-slate-300 transition-colors" data-id="element-136">
            Términos de Servicio
          </button>
        </div>
        <p data-id="element-137">
          © {new Date().getFullYear()} The Ghost Resume Engine. Todos los
          derechos reservados.
        </p>
      </footer>

      <AnimatePresence data-id="element-138">
        {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-139">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setActiveModal(null)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-140" />

            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-2xl max-h-[85vh] bg-navy-800 border border-navy-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-141">
              <div className="flex items-center justify-between p-6 border-b border-navy-700" data-id="element-142">
                <div data-id="element-143">
                  <h2 className="text-2xl font-display font-bold text-white" data-id="element-144">
                    {content.title}
                  </h2>
                  <p className="text-sm text-slate-400 mt-1" data-id="element-145">
                    {content.lastUpdated}
                  </p>
                </div>
                <button onClick={() => setActiveModal(null)} className="p-2 text-slate-400 hover:text-white hover:bg-navy-700 rounded-lg transition-colors" aria-label="Cerrar" data-id="element-146">
                  <X className="w-5 h-5" data-id="element-147" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto text-left" data-id="element-148">
                <div className="space-y-6" data-id="element-149">
                  {content.sections.map((section, idx) => <div key={idx} data-id="element-150">
                      <h3 className="text-lg font-bold text-white mb-2" data-id="element-151">
                        {section.heading}
                      </h3>
                      <p className="text-slate-300 leading-relaxed" data-id="element-152">
                        {section.text}
                      </p>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}